import { Component, Prop, Watch } from 'vue-property-decorator'
import Vue from '@src/vue'
import { CreateElement } from 'vue'

import { Modal } from '@src/elements/modal/modal'
import { Caption, Subtitle1, Subtitle2 } from '@src/elements/typography/typography'
import { Button } from '@src/elements/button/button'
import { Icon } from '@src/elements/icon/icon'
import { Textarea } from '@src/elements/input/textarea'
import { Radio } from '@src/elements/input/radio'
import ExerciseSelector from '@src/components/exercise-selector/exercise-selector'
import { ExerciseCardSelect } from '@src/components/card/exercise-card/exercise-card-select/exercise-card-select'

import { AffectedProduct, ExerciseSupportTicket, ExerciseSupportTicketDTO, StateRecording, StateTicket } from '@src/data/support-ticket'
import { API } from '@src/lib/api_path'
import ApiClient from '@src/lib/api_client'
import EventBus from '@src/lib/event_bus'
import { snakecaseKeys } from '@src/util/functions'
import { ExerciseResponse } from '@src/data/exercise-response'
import { WorkoutExercise } from '@src/data/workout-exercise'

import './ticket-modal.sass'
import { convertToLocalDate, convertYYYYMMDDToDate } from '@src/util/date-format'
import { ExpertiseLevel } from '@src/data/member'

@Component({
  computed: {
    availableExercises () {
      return this.$store.state.availableExercises
    },
    selectedExercises () {
      return this.$store.state.new_workout.workoutExercises
    }
  }
})
export class TicketModal extends Vue {

  @Prop({ required: true })
  private supportTicket: ExerciseSupportTicket

  @Prop({ required: true })
  private closeCallback: () => void

  @Prop({ required: true })
  private action: 'add' | 'edit'

  private availableExercises: ExerciseResponse[]
  private selectedExercises: WorkoutExercise[]

  private apiClient: ApiClient = new ApiClient()

  private currentSupportTicket: ExerciseSupportTicket = new ExerciseSupportTicket((this as TicketModal).supportTicket)
  private showAvailableExercises: boolean = false

  private newComment: string = ''

  private commentError: boolean = false

  private initialState: {
    exerciseId,
    problemType,
    feedbackLevel
    description
  }

  @Watch('supportTicket', { deep: true })
  supportTicketUpdated () {
    // re-render component only if video changes between periodic requests
    if (this.supportTicket.details.stateRecording !== this.currentSupportTicket.details.stateRecording) {
      this.currentSupportTicket.details.stateRecording = this.supportTicket.details.stateRecording
    }

    if (this.supportTicket.video !== this.currentSupportTicket.video) {
      this.currentSupportTicket.video = this.supportTicket.video
    }
  }

  beforeMount () {
    if (this.action !== 'edit') { return }

    this.setInitialState()
    this.currentSupportTicket.comments.sort(function (a, b) {
      return a.createdAt.localeCompare(b.createdAt)
    })
  }

  setInitialState () {
    this.initialState = {
      exerciseId: this.currentSupportTicket.details.exerciseId,
      problemType: this.currentSupportTicket.problemType,
      feedbackLevel: this.currentSupportTicket.feedbackLevel,
      description: this.currentSupportTicket.details.description
    }
  }

  renderComments (h: CreateElement) {
    return <div>
      <div class='commentsWrapper'>
        {this.currentSupportTicket.comments.map((comment) => {
          let author = comment.authorType.replace('_', '-')

          let formattedDate = convertYYYYMMDDToDate(comment.createdAt)
          let header = this.$t(comment.authorType === 'club' ?
            'SUPPORT_PAGE.SUPPORT_TICKET.MODAL.CLUB' : 'SUPPORT_PAGE.SUPPORT_TICKET.MODAL.SUPPORT_TEAM') + ': ' + convertToLocalDate(formattedDate)
          return <div class={'comment ' + author}>
            <div class='commentHeader'>{header}</div>
            <div class={'commentBody ' + author}>
              <div class='commentText'>
                {comment.body}
              </div>
            </div>
          </div>
        })}
      </div>
      <Textarea class={this.commentError ? 'error' : ''}
                value={this.newComment}
                label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.INSERT_COMMENT_HERE')}
                keyup={(value) => {
                  this.newComment = value
                  this.commentError = !value
                }} />
    </div>
  }

  renderTitle () {
    if (this.action === 'edit') {
      return this.currentSupportTicket.state === StateTicket.CLOSED ? this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_CLOSED') :
        this.currentSupportTicket.state === StateTicket.OPEN ? this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_RECEIVED') :
          this.currentSupportTicket.state === StateTicket.IN_PROGRESS ? this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_IN_PROGRESS') :
            this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.DRAFT_TICKET_DETAILS')
    } else {
      return this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.NEW_TICKET')
    }
  }

  showExercises () {
    this.showAvailableExercises = !this.showAvailableExercises
  }

  saveTicketExercise () {
    let exerciseId = this.selectedExercises[this.selectedExercises.length - 1].exerciseId
    if (exerciseId !== null) {
      this.currentSupportTicket.details.exerciseId = exerciseId
    }
    this.showExercises()
  }

  renderExercise (h: CreateElement) {
    const exercise = this.currentSupportTicket.details.exerciseId ? this.loadExercise(this.currentSupportTicket.details.exerciseId) : null

    return this.currentSupportTicket.state === StateTicket.DRAFT ? <div class='ticket-exercise'>
      <Subtitle1>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_EXERCISE')}</Subtitle1>
      <Caption>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_EXERCISE_DESCRIPTION')}</Caption>
      {exercise ?
        <ExerciseCardSelect ref='selected-exercise' exercise={exercise} type={'select'} exerciseCounter={0} /> : null}
      {exercise ?
        <Button type='elevated' disabled={!!this.currentSupportTicket.video} class='exercises-btn' label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.CHANGE_EXERCISE')}
                click={this.showExercises} /> :
        <Button type='elevated' class='exercises-btn' label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.SELECT_EXERCISE')} click={this.showExercises} />}
      {this.showAvailableExercises ?
        <Modal overflow={'visible'} show={true} title={this.$t('SUPPORT_PAGE.SELECT_EXERCISE')} class='available-exercises-modal'
               extraStyles={{ height: '65vh' }}
               closeCallback={this.showExercises}>
          <ExerciseSelector selectedExercises={[]} selectCard={true} />
          <div class='btn-row' slot='footer'>
            <Button type='outline' label={this.$t('CANCEL')} click={this.showExercises} />
            <Button class='save-btn' label={this.$t('SAVE')} disabled={this.selectedExercises.length === 0} click={this.saveTicketExercise} />
          </div>
        </Modal>
        : null}
    </div> : null
  }

  changeProblemType () {
    if (!this.currentSupportTicket.video) {
      if (this.currentSupportTicket.problemType === AffectedProduct.STATION_SOFTWARE) {
        this.currentSupportTicket.problemType = AffectedProduct.STATION_LEGACY
      } else {
        this.currentSupportTicket.problemType = AffectedProduct.STATION_SOFTWARE
      }
    }
  }

  renderProblemType (h: CreateElement) {
    return <div class='station-type'>
      <Subtitle1>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.STATION_TYPE')}</Subtitle1>
      <el-radio-group class={`station-type-options${this.currentSupportTicket.state !== StateTicket.DRAFT || this.currentSupportTicket.video ? ' disabled' : ''}`}>
        <Radio value={AffectedProduct.STATION_LEGACY} bindedProp={this.currentSupportTicket.problemType}
               change={this.changeProblemType}/>
        <Icon name='brand/3d'/>
        <Radio value={AffectedProduct.STATION_SOFTWARE} bindedProp={this.currentSupportTicket.problemType}
               change={this.changeProblemType}/>
        <Icon name='brand/2d'/>
      </el-radio-group>
    </div>
  }

  renderFeedbackLevel (h: CreateElement) {
    return this.currentSupportTicket.feedbackLevel ?
      <div class='feedback-level'>
        <Subtitle1>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.EXERCISE_DIFFICULTY')}</Subtitle1>
        <el-radio-group class={`feedback-level-options${this.currentSupportTicket.state !== StateTicket.DRAFT || this.currentSupportTicket.video ? ' disabled' : ''}`}>
          <Radio value={ExpertiseLevel.BEGINNER} bindedProp={this.currentSupportTicket.feedbackLevel}
                 text={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.BEGINNER')}
                 change={this.setFeedbackLevel}/>
          <Radio value={ExpertiseLevel.INTERMEDIATE} bindedProp={this.currentSupportTicket.feedbackLevel}
                 text={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.INTERMEDIATE')}
                 change={this.setFeedbackLevel}/>
          <Radio value={ExpertiseLevel.ADVANCED} bindedProp={this.currentSupportTicket.feedbackLevel}
                 text={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.ADVANCED')}
                 change={this.setFeedbackLevel}/>
        </el-radio-group>
      </div> : null
  }

  setFeedbackLevel (level) {
    if (level) this.currentSupportTicket.feedbackLevel = level
  }

  checkIfDisabled () {
    if (this.action === 'edit' && this.currentSupportTicket.video && this.currentSupportTicket.details.description) {
      return false
    }
    if (this.action === 'add' && this.currentSupportTicket.details.exerciseId && this.currentSupportTicket.details.description) {
      return false
    }
    return true
  }

  updateDisabled () {
    if (!this.initialState || this.action !== 'edit') {
      return false
    }
    return !(this.initialState.exerciseId !== this.currentSupportTicket.details.exerciseId
      || this.initialState.problemType !== this.currentSupportTicket.problemType
      || this.initialState.feedbackLevel !== this.currentSupportTicket.feedbackLevel
      || this.initialState.description !== this.currentSupportTicket.details.description)
  }

  addComment () {
    if (!this.newComment) {
      this.commentError = true
      return undefined
    }
    let closedTicket = this.currentSupportTicket.state === StateTicket.CLOSED
    return this.apiClient.post(`${API.TRAINERS_API.SUPPORT_TICKETS}/${this.currentSupportTicket.id}/${closedTicket ? 'reopen' : 'add_comment'}`,
      { comment: { body: this.newComment } })
      .then(() => {
        this.closeCallback()
        EventBus.trigger('reloadSupportTickets')
      })
      .catch(error => console.log('cannot ' + (closedTicket ? 'reopen ' : 'add comment to') + ' support ticket', error))
  }

  renderButtons (h: CreateElement) {
    return <div class='btn-row' slot='footer'>
      {this.currentSupportTicket.state !== StateTicket.CLOSED ? <Button type='outline' label={this.$t('DELETE')} click={this.deleteTicket} ref='closeBtn' /> : null}
      {this.action === 'edit' && this.currentSupportTicket.state === StateTicket.DRAFT ?
        <Button type='outline' label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.UPDATE_TICKET')} click={this.updateTicket} ref='updateBtn' disabled={this.updateDisabled()} /> : null}
      {this.currentSupportTicket.state === StateTicket.DRAFT ?
        <Button disabled={this.checkIfDisabled()} label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.SUBMIT_TICKET')}
                click={this.submitTicket} ref='submitBtn'/> :
        <Button class={!this.newComment ? 'disabled' : ''}
                label={this.$t(this.currentSupportTicket.state === StateTicket.CLOSED ?
                  'SUPPORT_PAGE.SUPPORT_TICKET.MODAL.REOPEN' : 'SUPPORT_PAGE.SUPPORT_TICKET.MODAL.ADD_COMMENT')}
                click={this.addComment}
                ref='submitBtn'/>}
    </div>

  }

  renderVideo (h: CreateElement) {
    return this.action === 'edit' ? <div>
      <Subtitle1 class='video-recording'>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.VIDEO_RECORDING')}</Subtitle1>
      {this.currentSupportTicket.state === StateTicket.DRAFT && this.currentSupportTicket.details.stateRecording !== StateRecording.FINISHED_AND_UPLOADED ?
        <div class='ticket-preview empty'>
          {this.currentSupportTicket.details.stateRecording === StateRecording.NOT_STARTED ?
            <Subtitle2 class='recording-status'><Icon name='util/not-recorded' />{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.NOT_RECORDED')}</Subtitle2> :
            this.currentSupportTicket.details.stateRecording === StateRecording.STARTED ?
              <Subtitle2 class='recording-status recording'><Icon name='util/recording' />{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.RECORDING')}</Subtitle2> :
              this.currentSupportTicket.details.stateRecording === StateRecording.FINISHED_AND_UPLOADING ?
                <Subtitle2 class='recording-status'><Icon name='util/circle-arrows' />{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.UPLOADING')}</Subtitle2> :
                null}
        </div> :
        <video class='ticket-preview' controls autoplay='autoplay' loop='loop'>
          {/*TODO remove -staging replacement; for now there is an error from the backend + old station*/}
          <source src={this.currentSupportTicket.video} type='video/mp4' />
          Error loading video.
        </video>
      }
    </div> : null
  }

  renderNotes (h: CreateElement) {
    return this.action === 'add' || (this.action === 'edit' && this.currentSupportTicket.state === StateTicket.DRAFT) ?
      <div class='ticket-notes'>
        <Subtitle1>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_NOTES')}</Subtitle1>
        <Caption class='ticket-notes-description'>
          {this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_NOTES_DESCRIPTION.GENERAL')}<br />
          ∙{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_NOTES_DESCRIPTION.1')}<br />
          ∙{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_NOTES_DESCRIPTION.2')}<br />
          ∙{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_NOTES_DESCRIPTION.3')}<br />
        </Caption>
        <Textarea value={this.currentSupportTicket.details.description} label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_NOTES_DETAILS')}
                  keyup={(value) => this.currentSupportTicket.details.description = value} />
      </div> :
      <div class='ticket-comments'>
        <Subtitle1>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.TICKET_NOTES')}</Subtitle1>
        {this.renderComments(h)}
      </div>
  }

  render (h: CreateElement) {
    return <Modal class='ticket-modal' overflow={'scrollable'} show={true} title={this.renderTitle()}
                  closeCallback={this.closeCallback}>
      {this.renderExercise(h)}
      {this.renderProblemType(h)}
      {this.renderFeedbackLevel(h)}
      {this.renderNotes(h)}
      {this.renderVideo(h)}
      {this.renderButtons(h)}
    </Modal>
  }

  deleteTicket () {
    return this.apiClient.delete(`${API.TRAINERS_API.SUPPORT_TICKETS}/${this.currentSupportTicket.id}`)
      .then(() => {
        this.closeCallback()
        EventBus.trigger('reloadSupportTickets')
      })
      .catch(error => console.log('cannot delete support ticket', error))
  }

  submitTicket () {
    if (this.action === 'edit') {
      return this.updateTicket().then(() => {
        this.apiClient.post(`${API.TRAINERS_API.SUPPORT_TICKETS}/${this.currentSupportTicket.id}/submit`,
          {})
          .then(() => {
            this.closeCallback()
            EventBus.trigger('reloadSupportTickets')
          })
          .catch(error => console.log('cannot submit support ticket', error))
      })
    } else {
      let newSupportTicket = snakecaseKeys(new ExerciseSupportTicketDTO(this.currentSupportTicket))
      newSupportTicket.details = snakecaseKeys(newSupportTicket.details)
      return this.apiClient.post(`${API.TRAINERS_API.SUPPORT_TICKETS}`,
        { support_ticket: newSupportTicket })
        .then(() => {
          this.closeCallback()
          EventBus.trigger('reloadSupportTickets')
        })
        .catch(error => console.log('cannot update support ticket', error))
    }
  }

  updateTicket () {
    let newSupportTicket = snakecaseKeys(new ExerciseSupportTicketDTO(this.currentSupportTicket))
    newSupportTicket.details = snakecaseKeys(newSupportTicket.details)
    return this.apiClient.put(`${API.TRAINERS_API.SUPPORT_TICKETS}/${this.currentSupportTicket.id}`,
      { support_ticket: newSupportTicket })
      .then(() => {
        // this.closeCallback()
        this.setInitialState()
        EventBus.trigger('reloadSupportTickets')
        this.$store.dispatch('showToast', { message: this.$t('SUPPORT_PAGE.SUPPORT_TICKET.UPDATED'), fadeOut: true })
      })
      .catch(error => console.log('cannot update support ticket', error))
  }

  private loadExercise (exerciseId: number): ExerciseResponse {
    return this.availableExercises.filter(exercise => exercise.id === exerciseId)[0]
  }
}
